import {
  getCustomColDefProperties,
  getMultiMarketDataSnapshotRequestKey,
  type SecurityMarketDataColumnParams,
  type UseMultiMarketDataSnapshotRequest,
} from '@talos/kyoko';
import type { Column } from 'ag-grid-community';
import { get } from 'lodash-es';
import type { PriceBlotterEntity } from './PriceBlotterEntity';

function getColumnMarkets(column: Column): string[] | undefined {
  const customColDefProperties = getCustomColDefProperties(column.getColDef());
  if (customColDefProperties?.talosColumnType !== 'securityMarketData') {
    return undefined;
  }

  const Markets = (customColDefProperties?.talosColumnParams as SecurityMarketDataColumnParams | undefined)?.Markets;
  return Array.isArray(Markets) && Markets.length > 0 ? Markets : undefined;
}

/**
 * Builds deduplicated MDS requests for all visible (column, row) combinations.
 *
 * Complexity: O(C * R), where C = visible columns and R = rendered items.
 * We iterate each visible column against each rendered row exactly once, and deduplicate
 * requests using a Map keyed by `(Symbol, Markets)` request key.
 *
 * Example:
 * visibleColumns:
 * - `security.Symbol` with Markets `['m2', 'm1']`
 * - `security.Symbol` with no market selector
 * renderedItems symbols: `BTC-USD`, `ETH-USD`
 * output requests:
 * - `{ key: 'BTC-USD|m1|m2', Symbol: 'BTC-USD', Markets: ['m2', 'm1'] }`
 * - `{ key: 'ETH-USD|m1|m2', Symbol: 'ETH-USD', Markets: ['m2', 'm1'] }`
 * - `{ key: 'BTC-USD', Symbol: 'BTC-USD', Markets: undefined }`
 * - `{ key: 'ETH-USD', Symbol: 'ETH-USD', Markets: undefined }`
 */
export function buildPriceBlotterMarketDataRequests({
  visibleColumns,
  renderedItems,
}: {
  visibleColumns: Column[];
  renderedItems: PriceBlotterEntity[];
}): UseMultiMarketDataSnapshotRequest[] {
  const requestsByKey = new Map<string, UseMultiMarketDataSnapshotRequest>();

  for (const column of visibleColumns) {
    for (const item of renderedItems) {
      const requestDetails = getPriceBlotterMarketDataRequest(column, item);
      if (requestDetails) {
        requestsByKey.set(requestDetails.key, requestDetails);
      }
    }
  }

  return [...requestsByKey.values()];
}

/** Given a column and an item (item being a rendered item) */
export function getPriceBlotterMarketDataRequest(
  column: Column,
  item: PriceBlotterEntity
): UseMultiMarketDataSnapshotRequest | undefined {
  const symbolPath = column.getColDef()?.field;
  if (!symbolPath) {
    return undefined;
  }

  const Symbol = get(item, symbolPath);
  if (typeof Symbol !== 'string' || Symbol.length === 0) {
    return undefined;
  }

  const Markets = getColumnMarkets(column);
  const key = getMultiMarketDataSnapshotRequestKey({ Symbol, Markets });
  return { key, Symbol, Markets };
}
